import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./metals.css";
import "../home/home.css";
import "../ai/chart.css";
import MetalsChart from "./MetalsChart";

const isLocal = typeof window !== "undefined" && window.location.hostname === "localhost";
const baseUrl = isLocal ? "http://localhost:5000" : "https://www.trendscout.club";

async function fetchAnalyses(metal, limit = 120) {
  const urlList = `${baseUrl}/api/precious-metals/analysis?metal=${metal}&limit=${limit}`;
  const r = await fetch(urlList);
  if (r.ok) return r.json();

  const urlLatest = `${baseUrl}/api/precious-metals/analysis/latest?metal=${metal}`;
  const r2 = await fetch(urlLatest);
  if (r2.ok) {
    const one = await r2.json();
    return [one];
  }
  throw new Error("Unable to fetch analyses.");
}

async function fetchOHLC(metal, limit = 360) {
  const url = `${baseUrl}/api/precious-metals/ohlc?metal=${metal}&limit=${limit}`;
  const r = await fetch(url);
  if (!r.ok) throw new Error("Unable to fetch OHLC.");
  return r.json(); // ascending order
}

function Badge({ children, tone = "neutral", title }) {
  return (
    <span className={`ms-badge ms-${tone}`} title={title}>
      {children}
    </span>
  );
}

function calcSMA(values, n) {
  if (!Array.isArray(values) || values.length < n) return null;
  let sum = 0;
  for (let i = values.length - n; i < values.length; i++) sum += Number(values[i]) || 0;
  return sum / n;
}


function ProbBars({ probs }) {
  const best = Number(probs?.best ?? probs?.up ?? 0);
  const base = Number(probs?.base ?? 0);
  const worst = Number(probs?.worst ?? probs?.down ?? 0);
  const sum = best + base + worst || 1;

  return (
    <div className="ms-prob-bars" title="Best / Base / Worst">
      <div className="ms-prob best" style={{ width: `${(best / sum) * 100}%` }}>
        <span>{best}%</span>
      </div>
      <div className="ms-prob base" style={{ width: `${(base / sum) * 100}%` }}>
        <span>{base}%</span>
      </div>
      <div className="ms-prob worst" style={{ width: `${(worst / sum) * 100}%` }}>
        <span>{worst}%</span>
      </div>
    </div>
  );
}

export default function MetalsDashboard() {
  const [metal, setMetal] = useState("XPT");
  const [analyses, setAnalyses] = useState([]);
  const [ohlcBars, setOhlcBars] = useState([]); 
  const [idx, setIdx] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const sel = analyses[idx];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const [list, ohlc] = await Promise.all([
          fetchAnalyses(metal, 120),
          fetchOHLC(metal, 360)
        ]);
        if (!mounted) return;
        const sorted = [...list].sort((a, b) => (a.asOf > b.asOf ? -1 : 1));
        setAnalyses(sorted);
        setIdx(0);
        setOhlcBars(Array.isArray(ohlc) ? ohlc : []);
      } catch (e) {
        console.error(e);
        setAnalyses([]);
        setOhlcBars([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [metal]);

const smaLines = useMemo(() => {
  // prefer indicators from selected analysis (if available)
  const ind = sel?.indicators;
  if (ind) {
    return [
      { key: "sma20",  val: ind.sma20,  color: "#66bb6a", label: "SMA20" },
      { key: "sma50",  val: ind.sma50,  color: "#29b6f6", label: "SMA50" },
      { key: "sma200", val: ind.sma200, color: "#ef5350", label: "SMA200" },
    ].filter(x => Number.isFinite(x.val));
  }
  // fallback: compute from OHLC closes
  const closes = (ohlcBars || []).map(b => Number(b.close)).filter(Number.isFinite);
  const s20  = calcSMA(closes, 20);
  const s50  = calcSMA(closes, 50);
  const s200 = calcSMA(closes, 200);
  return [
    Number.isFinite(s20)  && { key: "sma20",  val: s20,  color: "#66bb6a", label: "SMA20" },
    Number.isFinite(s50)  && { key: "sma50",  val: s50,  color: "#29b6f6", label: "SMA50" },
    Number.isFinite(s200) && { key: "sma200", val: s200, color: "#ef5350", label: "SMA200" },
  ].filter(Boolean);
}, [sel, ohlcBars]);

const lastBar = useMemo(() => {
  const arr = Array.isArray(ohlcBars) ? ohlcBars : [];
  return arr.length ? arr[arr.length - 1] : null;
}, [ohlcBars]);


  const sr = sel?.structure || { supports: [], resistances: [] };

  return (
    <div className="ai-suggestion-list">
      <div className="single-suggestion-box">
        <div className="ms-header-row">
          <div className="ms-tabs">
            {["XPT", "XPD", "XRH"].map(m => (
              <button
                key={m}
                className={`ms-tab ${metal === m ? "active" : ""}`}
                onClick={() => setMetal(m)}
                disabled={loading}
              >
                {m === "XPT" ? "Platinum (XPT)" : m === "XPD" ? "Palladium (XPD)" : "Rhodium (XRH)"}
              </button>
            ))}
            <button
              className="ms-tab"
              onClick={() => navigate("/embeds/metals")}
              title="Get copy-paste widget code"
            >
              ⧉ Get Embed Codes
            </button>
          </div>

          <div className="ms-date-nav">
            <button
              onClick={() => setIdx(i => Math.min(i + 1, Math.max(analyses.length - 1, 0)))}
              disabled={idx >= analyses.length - 1 || loading}
              className="page-button"
            >
              ◀ Prev
            </button>
            <select
              className="ms-date-select"
              value={idx}
              onChange={(e) => setIdx(Number(e.target.value))}
              disabled={loading || analyses.length === 0}
            >
              {analyses.map((a, i) => (
                <option value={i} key={`${a.metal}-${a.asOf}-${i}`}>
                  {a.asOf} ({a.metal})
                </option>
              ))}
            </select>
            <button
              onClick={() => setIdx(i => Math.max(i - 1, 0))}
              disabled={idx === 0 || loading}
              className="page-button"
            >
              Next ▶
            </button>
          </div>
        </div>

        {loading && <div className="loading-banner">Loading {metal}…</div>}
        {!loading && !sel && (
        <div className="info-banner">
            No analyses found yet — showing price chart from OHLC history.
        </div>
        )}


        {(ohlcBars?.length > 0) && (
          <>
            <div className="details-panel ms-top-cards">
            {sel ? (
                <>
                <div className="ms-top-card">
                    <div className="ms-k">Date</div>
                    <div className="ms-v">{sel.asOf}</div>
                </div>
                <div className="ms-top-card">
                    <div className="ms-k">Spot</div>
                    <div className="ms-v">${Number(sel.spot).toLocaleString()}</div>
                </div>
                <div className="ms-top-card">
                    <div className="ms-k">Bias (MACD)</div>
                    <div className="ms-v">
                    {sel?.indicators?.macdCross
                        ? <Badge tone={sel.indicators.macdCross === "bullish" ? "green" : "red"}>
                            {sel.indicators.macdCross}
                        </Badge>
                        : "—"}
                    </div>
                </div>
                <div className="ms-top-card">
                    <div className="ms-k">RSI(14)</div>
                    <div className="ms-v">
                    {sel?.indicators?.rsi14 != null
                        ? <Badge tone="amber">{sel.indicators.rsi14}</Badge>
                        : "—"}
                    </div>
                </div>
                </>
            ) : (
                // Fallback summary from raw OHLC if no analysis yet
                <>
                <div className="ms-top-card">
                    <div className="ms-k">Date</div>
                    <div className="ms-v">{lastBar?.date ?? "—"}</div>
                </div>
                <div className="ms-top-card">
                    <div className="ms-k">Spot</div>
                    <div className="ms-v">
                    {lastBar?.close != null ? `$${Number(lastBar.close).toLocaleString()}` : "—"}
                    </div>
                </div>
                <div className="ms-top-card">
                    <div className="ms-k">Bias (MACD)</div>
                    <div className="ms-v">—</div>
                </div>
                <div className="ms-top-card">
                    <div className="ms-k">RSI(14)</div>
                    <div className="ms-v">—</div>
                </div>
                </>
            )}
            </div>


            <div className="details-panel">
             <MetalsChart
                // stats: if sel exists use it; else minimal fallback so title shows metal
                stats={sel?.stats || sel || { metal }}
                bars={ohlcBars}                // <— use OHLC from DB
                smaLines={smaLines}
                supports={(sel?.structure?.supports) || []}
                resistances={(sel?.structure?.resistances) || []}
              />
            </div>

            <div className="details-panel ms-horizons">
              <h3>Predictions</h3>
              <div className="ms-h-grid">
                {["7d", "30d"].map(h => {
                  const block = sel?.horizons?.[h];
                  if (!block) return (
                    <div className="ms-h-card" key={h}>
                      <div className="ms-h-title">{h.toUpperCase()}</div>
                      <div className="ms-h-empty">No data</div>
                    </div>
                  );
                  const aiOut =
                    h === "7d" ? sel?.ai?.output?.sevenDay : sel?.ai?.output?.thirtyDay;
                  const bias = aiOut?.bias || "—";
                  const narrative = aiOut?.narrative || "";
                  const probs = aiOut?.probabilities || block?.probabilities;

                  return (
                    <div className="ms-h-card" key={h}>
                      <div className="ms-h-title">
                        {h.toUpperCase()}{" "}
                        {bias !== "—" && (
                          <Badge tone={
                            bias === "Bullish" ? "green" : bias === "Bearish" ? "red" : "blue"
                          }>
                            {bias}
                          </Badge>
                        )}
                      </div>

                      <div className="ms-rows">
                        <div className="ms-row">
                          <span className="ms-k">Best</span>
                          <span className="ms-v">
                            ${block.bestCase?.price ?? aiOut?.best?.price} (
                            {(block.bestCase?.changePct ?? aiOut?.best?.changePct)?.toFixed?.(2)}%)
                          </span>
                        </div>
                        <div className="ms-row">
                          <span className="ms-k">Base</span>
                          <span className="ms-v">
                            ${block.baseCase?.price ?? aiOut?.base?.price} (
                            {(block.baseCase?.changePct ?? aiOut?.base?.changePct)?.toFixed?.(2)}%)
                          </span>
                        </div>
                        <div className="ms-row">
                          <span className="ms-k">Worst</span>
                          <span className="ms-v">
                            ${block.worstCase?.price ?? aiOut?.worst?.price} (
                            {(block.worstCase?.changePct ?? aiOut?.worst?.changePct)?.toFixed?.(2)}%)
                          </span>
                        </div>
                      </div>

                      <ProbBars probs={probs} />

                      {narrative && (
                        <div className="ms-narrative">
                          {narrative}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="details-panel ms-sr-wrap">
              <h3>Nearest Support / Resistance</h3>
              <div className="ms-sr-grid">
                <div>
                  <div className="ms-k">Supports</div>
                  <div className="ms-tags">
                    {(sr.supports || []).slice(0,4).map((v,i) => (
                      <Badge key={i} tone="green">${v}</Badge>
                    ))}
                    {(!sr.supports || sr.supports.length === 0) && <span>—</span>}
                  </div>
                </div>
                <div>
                  <div className="ms-k">Resistances</div>
                  <div className="ms-tags">
                    {(sr.resistances || []).slice(0,4).map((v,i) => (
                      <Badge key={i} tone="red">${v}</Badge>
                    ))}
                    {(!sr.resistances || sr.resistances.length === 0) && <span>—</span>}
                  </div>
                </div>
              </div>
            </div>

            <div className="details-panel ms-ai-meta">
              <h3>AI Summary</h3>
              <div className="ms-ai-grid">
                <div>
                  <div className="ms-k">Enabled</div>
                  <div className="ms-v">{sel?.ai?.enabled ? "Yes" : "No"}</div>
                </div>
                <div>
                  <div className="ms-k">Model</div>
                  <div className="ms-v">CRG LLM</div>
                </div>
                <div>
                  <div className="ms-k">Temp</div>
                  <div className="ms-v">0.2</div>
                </div>
              </div>

              {sel?.ai?.output?._raw && (
                <details className="ms-raw">
                  <summary>Raw AI JSON</summary>
                  <pre className="ms-pre">{JSON.stringify(sel.ai.output._raw, null, 2)}</pre>
                </details>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
